import React, { useEffect, useState } from 'react';
import { Save, Phone, MessageCircle, Instagram, Facebook } from 'lucide-react';
import api from '../utils/api';

interface ContactInfoForm {
  whatsappNumber: string;
  instagramUrl: string;
  facebookUrl: string;
  phoneNumber: string;
}

const initialForm: ContactInfoForm = {
  whatsappNumber: '',
  instagramUrl: '',
  facebookUrl: '',
  phoneNumber: ''
};

const AdminContactInfo: React.FC = () => {
  const [form, setForm] = useState<ContactInfoForm>(initialForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchContactInfo = async () => {
      setLoading(true);
      try {
        const { data } = await api.get('/api/contact-info');
        setForm({
          whatsappNumber: data?.whatsappNumber || '',
          instagramUrl: data?.instagramUrl || '',
          facebookUrl: data?.facebookUrl || '',
          phoneNumber: data?.phoneNumber || ''
        });
      } catch (error) {
        console.error('Error fetching contact info:', error);
        alert('فشل تحميل معلومات التواصل.');
      } finally {
        setLoading(false);
      }
    };

    fetchContactInfo();
  }, []);

  const updateField = (field: keyof ContactInfoForm, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await api.put('/api/contact-info/admin', form);
      setForm({
        whatsappNumber: data?.whatsappNumber || '',
        instagramUrl: data?.instagramUrl || '',
        facebookUrl: data?.facebookUrl || '',
        phoneNumber: data?.phoneNumber || ''
      });
      alert('تم حفظ معلومات التواصل بنجاح.');
    } catch (error: any) {
      console.error('Error saving contact info:', error);
      alert(error?.response?.data?.error || 'فشل حفظ معلومات التواصل.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 bg-gradient-to-b from-pink-50 via-rose-50 to-pink-100 dark:bg-dark-bg dark:from-dark-bg dark:via-dark-bg dark:to-dark-bg min-h-screen transition-colors duration-300"
        dir="rtl"
      >
        <div className="text-center text-gray-600 dark:text-dark-textSecondary">جاري تحميل معلومات التواصل...</div>
      </div>
    );
  }

  return (
    <div
      className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 bg-gradient-to-b from-pink-50 via-rose-50 to-pink-100 dark:bg-dark-bg dark:from-dark-bg dark:via-dark-bg dark:to-dark-bg min-h-screen transition-colors duration-300"
      dir="rtl"
    >
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-dark-text">تعديل معلومات التواصل</h1>
        <p className="text-gray-600 dark:text-dark-textSecondary mt-2">
          المعلومات التي تدخلها هنا ستظهر مباشرة في زر "تواصل معنا" داخل الفوتر.
        </p>
      </div>

      <div className="bg-white dark:bg-dark-surface rounded-xl border border-gray-200 dark:border-dark-border shadow-sm p-5 sm:p-6 space-y-5 transition-colors duration-300">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-dark-textSecondary mb-2">
            <span className="inline-flex items-center">
              <MessageCircle className="h-4 w-4 ml-2 text-green-600" />
              واتساب
            </span>
          </label>
          <input
            type="text"
            value={form.whatsappNumber}
            onChange={(e) => updateField('whatsappNumber', e.target.value)}
            placeholder="+9647XXXXXXXX"
            className="w-full p-3 border border-gray-300 dark:border-dark-border rounded-lg bg-white dark:bg-dark-hover text-gray-900 dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-dark-textSecondary mb-2">
            <span className="inline-flex items-center">
              <Instagram className="h-4 w-4 ml-2 text-pink-600" />
              إنستغرام
            </span>
          </label>
          <input
            type="url"
            value={form.instagramUrl}
            onChange={(e) => updateField('instagramUrl', e.target.value)}
            placeholder="https://instagram.com/your-page"
            className="w-full p-3 border border-gray-300 dark:border-dark-border rounded-lg bg-white dark:bg-dark-hover text-gray-900 dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-dark-textSecondary mb-2">
            <span className="inline-flex items-center">
              <Facebook className="h-4 w-4 ml-2 text-blue-600" />
              فيسبوك
            </span>
          </label>
          <input
            type="url"
            value={form.facebookUrl}
            onChange={(e) => updateField('facebookUrl', e.target.value)}
            placeholder="https://facebook.com/your-page"
            className="w-full p-3 border border-gray-300 dark:border-dark-border rounded-lg bg-white dark:bg-dark-hover text-gray-900 dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-dark-textSecondary mb-2">
            <span className="inline-flex items-center">
              <Phone className="h-4 w-4 ml-2 text-gray-700 dark:text-dark-text" />
              رقم الهاتف
            </span>
          </label>
          <input
            type="text"
            value={form.phoneNumber}
            onChange={(e) => updateField('phoneNumber', e.target.value)}
            placeholder="+9647XXXXXXXX"
            className="w-full p-3 border border-gray-300 dark:border-dark-border rounded-lg bg-white dark:bg-dark-hover text-gray-900 dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="w-full bg-pink-600 hover:bg-pink-700 disabled:opacity-60 text-white font-medium py-3 px-4 rounded-lg flex items-center justify-center transition-colors"
        >
          <Save className="h-4 w-4 ml-2" />
          {saving ? 'جاري الحفظ...' : 'حفظ معلومات التواصل'}
        </button>
      </div>
    </div>
  );
};

export default AdminContactInfo;
